"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useMemo } from "react";
import DropContext from "./DropContext";
import type { DropIdentityMode, DropInteractionParams, DropLocation, DropTimestampLayout } from "./drop.types";
import ParticipationDrop from "./participation/ParticipationDrop";
import WaveDrop from "./WaveDrop";
import WinnerDrop from "./winner/WinnerDrop";
export type { DropInteractionParams } from "./drop.types";
export { DropLocation } from "./drop.types";
interface DropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: DropType | null;
  readonly nextDrop: DropType | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly wrapContentOnly?: (content: React.ReactNode) => React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function Drop(t0) {
  const $ = _c(75);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef,
    wrapContentOnly,
    footer,
    identityMode,
    timestampLayout,
    showInteractions: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat;
  const openDropContentClick = canOpenDrop ? onDropContentClick : undefined;
  let t4;
  if ($[0] !== activeDrop || $[1] !== drop || $[2] !== embedDepth || $[3] !== embedPath || $[4] !== footer || $[5] !== identityMode || $[6] !== isVotingClosed || $[7] !== isVotingControlsLocked || $[8] !== location || $[9] !== maxEmbedDepth || $[10] !== onQuoteClick || $[11] !== onReply || $[12] !== openDropContentClick || $[13] !== parentContainerRef || $[14] !== quotePath || $[15] !== showInteractions || $[16] !== showReplyAndQuote || $[17] !== showWaveInfo || $[18] !== timestampLayout || $[19] !== winningThreshold) {
    t4 = <ParticipationDrop drop={drop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} onReply={onReply} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[0] = activeDrop;
    $[1] = drop;
    $[2] = embedDepth;
    $[3] = embedPath;
    $[4] = footer;
    $[5] = identityMode;
    $[6] = isVotingClosed;
    $[7] = isVotingControlsLocked;
    $[8] = location;
    $[9] = maxEmbedDepth;
    $[10] = onQuoteClick;
    $[11] = onReply;
    $[12] = openDropContentClick;
    $[13] = parentContainerRef;
    $[14] = quotePath;
    $[15] = showInteractions;
    $[16] = showReplyAndQuote;
    $[17] = showWaveInfo;
    $[18] = timestampLayout;
    $[19] = winningThreshold;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== activeDrop || $[22] !== drop || $[23] !== dropViewDropId || $[24] !== embedDepth || $[25] !== embedPath || $[26] !== footer || $[27] !== identityMode || $[28] !== location || $[29] !== maxEmbedDepth || $[30] !== nextDrop || $[31] !== onQuoteClick || $[32] !== onReply || $[33] !== onReplyClick || $[34] !== openDropContentClick || $[35] !== parentContainerRef || $[36] !== previousDrop || $[37] !== quotePath || $[38] !== showInteractions || $[39] !== showReplyAndQuote || $[40] !== showWaveInfo || $[41] !== timestampLayout) {
    t5 = <WinnerDrop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[21] = activeDrop;
    $[22] = drop;
    $[23] = dropViewDropId;
    $[24] = embedDepth;
    $[25] = embedPath;
    $[26] = footer;
    $[27] = identityMode;
    $[28] = location;
    $[29] = maxEmbedDepth;
    $[30] = nextDrop;
    $[31] = onQuoteClick;
    $[32] = onReply;
    $[33] = onReplyClick;
    $[34] = openDropContentClick;
    $[35] = parentContainerRef;
    $[36] = previousDrop;
    $[37] = quotePath;
    $[38] = showInteractions;
    $[39] = showReplyAndQuote;
    $[40] = showWaveInfo;
    $[41] = timestampLayout;
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  const t6 = previousDrop?.type === DropSize.FULL ? previousDrop : null;
  const t7 = nextDrop?.type === DropSize.FULL ? nextDrop : null;
  let t8;
  if ($[43] !== activeDrop || $[44] !== drop || $[45] !== dropViewDropId || $[46] !== embedDepth || $[47] !== embedPath || $[48] !== footer || $[49] !== identityMode || $[50] !== location || $[51] !== maxEmbedDepth || $[52] !== onQuoteClick || $[53] !== onReply || $[54] !== onReplyClick || $[55] !== openDropContentClick || $[56] !== quotePath || $[57] !== showInteractions || $[58] !== showReplyAndQuote || $[59] !== showWaveInfo || $[60] !== t6 || $[61] !== t7 || $[62] !== timestampLayout || $[63] !== wrapContentOnly) {
    t8 = <WaveDrop drop={drop} previousDrop={t6} nextDrop={t7} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} wrapContentOnly={wrapContentOnly} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[43] = activeDrop;
    $[44] = drop;
    $[45] = dropViewDropId;
    $[46] = embedDepth;
    $[47] = embedPath;
    $[48] = footer;
    $[49] = identityMode;
    $[50] = location;
    $[51] = maxEmbedDepth;
    $[52] = onQuoteClick;
    $[53] = onReply;
    $[54] = onReplyClick;
    $[55] = openDropContentClick;
    $[56] = quotePath;
    $[57] = showInteractions;
    $[58] = showReplyAndQuote;
    $[59] = showWaveInfo;
    $[60] = t6;
    $[61] = t7;
    $[62] = timestampLayout;
    $[63] = wrapContentOnly;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  let t9;
  if ($[65] !== t4 || $[66] !== t5 || $[67] !== t8) {
    t9 = {
      [ApiDropType.Participatory]: t4,
      [ApiDropType.Winner]: t5,
      [ApiDropType.Chat]: t8
    };
    $[65] = t4;
    $[66] = t5;
    $[67] = t8;
    $[68] = t9;
  } else {
    t9 = $[68];
  }
  const components = t9;
  let t10;
  if ($[69] !== drop || $[70] !== location) {
    t10 = {
      drop,
      location
    };
    $[69] = drop;
    $[70] = location;
    $[71] = t10;
  } else {
    t10 = $[71];
  }
  const memoizedValue = t10;
  const t11 = components[drop.drop_type];
  let t12;
  if ($[72] !== memoizedValue || $[73] !== t11) {
    t12 = <DropContext.Provider value={memoizedValue}>{t11}</DropContext.Provider>;
    $[72] = memoizedValue;
    $[73] = t11;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  return t12;
}